// src/pages/Settings.js — ADDED: Dispatch event after settings change
// + Party Master import: drag-and-drop + column mapping (Oct 2026)
import React, { useState, useEffect, useRef } from 'react';
import { get, post, patch } from '../utils/api';

// Party Master CSV field requirements — mirrors EXPECTED_HEADER in the
// backend's routes/partyMaster.js exactly. The uploaded file's actual
// column names don't need to match these — the mapping UI below lets
// the user map whatever headers their file has onto these canonical
// names, which then get written into the header row sent to the
// backend. Data rows are never touched, only the header line.
const PARTY_MASTER_FIELDS = [
  { key: 'party_code', label: 'Party Code (Marg CID)' },
  { key: 'name', label: 'Party Name' },
  { key: 'city', label: 'City' },
  { key: 'address_line1', label: 'Address Line 1' },
  { key: 'address_line2', label: 'Address Line 2' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'phone', label: 'Phone' },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'area_name', label: 'Area Name' },
  { key: 'station', label: 'Station' },
  { key: 'state', label: 'State' },
  { key: 'updated_at', label: 'Updated At' },
];

// Minimal CSV line parser — same quote-handling approach as the backend's
// parseCsvLine in routes/partyMaster.js, kept in sync deliberately so a
// header row round-trips the same way on both sides.
function parseCsvLine(line) {
  const fields = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuotes = false;
      } else {
        cur += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { fields.push(cur); cur = ''; }
      else cur += c;
    }
  }
  fields.push(cur);
  return fields;
}

export default function Settings() {
  const user = JSON.parse(localStorage.getItem('dms_user') || '{}');
  const [settings, setSettings] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [savingCompanyName, setSavingCompanyName] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Party Master import — see routes/partyMaster.js. This backend has no
  // Marg invoice sync, so this CSV import is the primary way party data
  // (name, address, phone, station, real erp_party_id/CID) gets into the
  // system, which QR-scan ingestion then matches against.
  //
  // Drag-and-drop + column mapping: the uploaded file's actual header
  // row is read as soon as it's dropped/selected (csvHeaders), and
  // auto-matched against PARTY_MASTER_FIELDS where names line up
  // exactly (the common case — Marg's real export already matches, so
  // the mapping pre-fills completely and Import is a single click,
  // same as before). Any file with different column names can still be
  // mapped by hand before import. The full file text is cached once
  // (csvText) so import doesn't need to re-read the file.
  const [partyMasterFile, setPartyMasterFile] = useState(null);
  const [csvText, setCsvText] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [partyStats, setPartyStats] = useState(null);
  const fileInputRef = useRef(null);

  // Pincode config — Settings items 10.1/10.2. Previously this had NO UI
  // at all (only a direct-DB-write path existed), and the classification
  // logic silently mishandled non-contiguous real data. See dispatch.js's
  // classifyByPincode() for the actual fix.
  const [pincodeFrom, setPincodeFrom] = useState('');
  const [pincodeTo, setPincodeTo] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [newExceptionPin, setNewExceptionPin] = useState('');
  const [newExceptionType, setNewExceptionType] = useState('instation');
  const [pincodeSaving, setPincodeSaving] = useState(false);
  const [pincodeMessage, setPincodeMessage] = useState('');

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await get('/api/settings');
        setSettings(response.settings || []);

        const companyNameSetting = (response.settings || []).find(s => s.key === 'company_name');
        if (companyNameSetting) setCompanyName(companyNameSetting.value);
      } catch (err) {
        console.error('Error loading settings:', err);
        setMessage('Error loading settings');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
    loadPartyStats();
    loadPincodeConfig();
  }, []);

  const loadPincodeConfig = async () => {
    try {
      const cfg = await get('/api/settings/pincode-config');
      setPincodeFrom(cfg.from || '');
      setPincodeTo(cfg.to || '');
      setExceptions(cfg.exceptions || []);
    } catch (err) {
      console.error('Error loading pincode config:', err);
    }
  };

  function addException() {
    const pin = newExceptionPin.trim();
    if (!/^\d{6}$/.test(pin)) {
      setPincodeMessage('❌ Pincode must be exactly 6 digits');
      return;
    }
    if (exceptions.some(e => e.pincode === pin)) {
      setPincodeMessage('❌ That pincode already has an exception — remove it first to change the type');
      return;
    }
    setExceptions([...exceptions, { pincode: pin, type: newExceptionType }]);
    setNewExceptionPin('');
    setPincodeMessage('');
  }

  function removeException(pin) {
    setExceptions(exceptions.filter(e => e.pincode !== pin));
  }

  async function savePincodeConfig() {
    setPincodeSaving(true);
    setPincodeMessage('');
    try {
      await post('/api/settings/pincode-config', { from: pincodeFrom, to: pincodeTo, exceptions });
      setPincodeMessage('✅ Pincode rules saved');
      notifySettingsChanged();
      setTimeout(() => setPincodeMessage(''), 5000);
    } catch (err) {
      setPincodeMessage(`❌ Error: ${err.message}`);
    } finally {
      setPincodeSaving(false);
    }
  }

  const loadPartyStats = async () => {
    try {
      const stats = await get('/api/party-master/stats');
      setPartyStats(stats);
    } catch (err) {
      console.error('Error loading party master stats:', err);
    }
  };

  // Reads the dropped/selected file once, caches its full text, parses
  // just the header line, and auto-matches it against PARTY_MASTER_FIELDS.
  function readAndMapFile(file) {
    if (!file) return;
    setPartyMasterFile(file);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setCsvText(text);
      const firstLine = text.split(/\r?\n/)[0] || '';
      const headers = parseCsvLine(firstLine).map(h => h.trim());
      setCsvHeaders(headers);

      const autoMap = {};
      PARTY_MASTER_FIELDS.forEach(f => {
        const match = headers.find(h => h.toLowerCase() === f.key.toLowerCase());
        if (match) autoMap[f.key] = match;
      });
      setColumnMapping(autoMap);
    };
    reader.onerror = () => {
      setImportResult({ error: 'Failed to read file' });
    };
    reader.readAsText(file);
  }

  function handleFileSelect(e) {
    readAndMapFile(e.target.files?.[0] || null);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readAndMapFile(file);
  }

  function updateMapping(fieldKey, headerValue) {
    setColumnMapping(prev => ({ ...prev, [fieldKey]: headerValue }));
  }

  // Explicit "Remove" action — clears everything, including any
  // leftover result message from a previous attempt.
  function clearPartyMasterFile() {
    setPartyMasterFile(null);
    setCsvText('');
    setCsvHeaders([]);
    setColumnMapping({});
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const allFieldsMapped = PARTY_MASTER_FIELDS.every(f => columnMapping[f.key]);
  const mappedValues = Object.values(columnMapping);
  const hasDuplicateMapping = new Set(mappedValues).size !== mappedValues.length;
  const canImport = Boolean(partyMasterFile) && allFieldsMapped && !hasDuplicateMapping && !importing;

  function handleImportPartyMaster() {
    if (!canImport) return;
    setImporting(true);
    setImportResult(null);
    try {
      const lines = csvText.split(/\r?\n/);
      const originalHeader = parseCsvLine(lines[0] || '').map(h => h.trim());

      // Build actualHeader -> canonicalFieldName, then rewrite ONLY the
      // header line — every data row is sent exactly as uploaded, since
      // the backend matches columns by name, not position. Any column
      // in the file that wasn't mapped to one of our 12 required fields
      // is left under its original name and simply ignored server-side.
      const reverseMap = {};
      PARTY_MASTER_FIELDS.forEach(f => {
        const actual = columnMapping[f.key];
        if (actual) reverseMap[actual] = f.key;
      });
      const newHeader = originalHeader.map(h => reverseMap[h] || h);
      lines[0] = newHeader.join(',');
      const remappedCsv = lines.join('\n');

      post('/api/party-master/import', { csv: remappedCsv })
        .then(result => {
          setImportResult(result);
          loadPartyStats();
          setPartyMasterFile(null);
          setCsvText('');
          setCsvHeaders([]);
          setColumnMapping({});
          if (fileInputRef.current) fileInputRef.current.value = '';
        })
        .catch(err => {
          setImportResult({ error: err.message });
        })
        .finally(() => {
          setImporting(false);
        });
    } catch (err) {
      setImportResult({ error: 'Failed to process file: ' + err.message });
      setImporting(false);
    }
  }

  // ADDED: Helper function to dispatch settings change event across tabs
  const notifySettingsChanged = () => {
    // Use localStorage to broadcast across tabs
    localStorage.setItem('dms-settings-changed', Date.now().toString());
    // Also dispatch local event for same tab
    window.dispatchEvent(new CustomEvent('dms-settings-changed', { detail: { timestamp: Date.now() } }));
  };

  // Update company name shown on printed delivery labels
  const handleUpdateCompanyName = async () => {
    if (user.role !== 'owner') {
      setMessage('Only owners can change company name');
      return;
    }
    if (companyName.trim().length === 0) {
      setMessage('Company name cannot be empty');
      return;
    }

    setSavingCompanyName(true);
    try {
      await post('/api/settings', { key: 'company_name', value: companyName.trim() });
      setMessage('✅ Company name updated');
      notifySettingsChanged();
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Error updating company name:', err);
      setMessage(`❌ Error: ${err.message || 'Failed to save'}`);
    } finally {
      setSavingCompanyName(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  // Only show invoice flow controls if user is owner
  const isOwner = user.role === 'owner';

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage system configuration and invoice flow</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Party Master Import — Owner Only */}
      {isOwner && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-2 text-brand-700">Party Master Import</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Drag and drop a Party Master CSV export to load or refresh party records (name, address, phone,
            station, real Marg party code). This backend doesn't sync invoices from Marg directly — QR-scan
            ingestion matches against these imported records. Column names don't need to match exactly — map
            them below after choosing a file.
          </p>

          {partyStats && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {partyStats.total || 0} parties currently on file
              {partyStats.last_updated && ` · last updated ${new Date(partyStats.last_updated).toLocaleString('en-IN')}`}
            </p>
          )}

          {!partyMasterFile ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-brand-700 bg-brand-700/5'
                  : 'border-gray-300 dark:border-gray-600 hover:border-brand-500'
              }`}
            >
              <p className="text-gray-600 dark:text-gray-300 font-medium mb-1">
                Drag and drop a CSV file here, or click to browse
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">.csv files only</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  📄 {partyMasterFile.name} · {csvHeaders.length} column{csvHeaders.length === 1 ? '' : 's'} found
                </span>
                <button onClick={clearPartyMasterFile} className="text-xs text-red-600 hover:underline">
                  Remove
                </button>
              </div>

              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Map Columns {allFieldsMapped ? '✓' : `(${PARTY_MASTER_FIELDS.filter(f => columnMapping[f.key]).length}/${PARTY_MASTER_FIELDS.length} mapped)`}
              </h3>
              {hasDuplicateMapping && (
                <p className="text-xs text-red-600 mb-2">
                  Two fields are mapped to the same column — each field needs its own column.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {PARTY_MASTER_FIELDS.map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                      {field.label}
                    </label>
                    <select
                      value={columnMapping[field.key] || ''}
                      onChange={e => updateMapping(field.key, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${
                        columnMapping[field.key]
                          ? 'border-gray-300 dark:border-gray-600'
                          : 'border-red-300 bg-red-50 dark:bg-red-900/10'
                      }`}
                    >
                      <option value="">-- not mapped --</option>
                      {csvHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <button
                onClick={handleImportPartyMaster}
                disabled={!canImport}
                className="px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-50"
              >
                {importing ? 'Importing...' : 'Import CSV'}
              </button>
            </div>
          )}

          {importResult && (
            <div className={`mt-4 p-4 rounded-lg text-sm ${importResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
              {importResult.error ? (
                <p>{importResult.error}</p>
              ) : (
                <>
                  <p>{importResult.imported} new, {importResult.updated} updated, {importResult.failed} failed</p>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <ul className="mt-2 text-xs text-red-600 list-disc list-inside">
                      {importResult.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e.row}: {e.error}</li>
                      ))}
                      {importResult.errors.length > 10 && <li>...and {importResult.errors.length - 10} more</li>}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Instation/Outstation Pincode Rules — Owner Only */}
      {isOwner && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-2 text-brand-700">Instation / Outstation Pincode Rules</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            New invoices are auto-classified after packing. A pincode inside the range below counts as
            instation by default — the exceptions list below overrides that for specific pincodes in either
            direction (useful since real delivery-area pincodes aren't always one contiguous block).
          </p>

          {pincodeMessage && (
            <div className={`p-3 rounded-lg mb-4 text-sm ${pincodeMessage.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {pincodeMessage}
            </div>
          )}

          <div className="flex items-end gap-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Instation Range — From</label>
              <input
                type="text"
                value={pincodeFrom}
                onChange={e => setPincodeFrom(e.target.value)}
                placeholder="e.g. 570000"
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">To</label>
              <input
                type="text"
                value={pincodeTo}
                onChange={e => setPincodeTo(e.target.value)}
                placeholder="e.g. 570099"
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
              />
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Exceptions ({exceptions.length})</h3>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
            {exceptions.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">No exceptions configured</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {/* NOTE: instation/outstation badge colors left
                      untouched — same categorical color-code used
                      throughout the app */}
                  {exceptions.map(e => (
                    <tr key={e.pincode} className="border-t first:border-t-0">
                      <td className="px-4 py-2 font-mono">{e.pincode}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${e.type === 'instation' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {e.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeException(e.pincode)} className="text-xs text-red-600 hover:underline">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-end gap-3 mb-6">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Pincode</label>
              <input
                type="text"
                value={newExceptionPin}
                onChange={e => setNewExceptionPin(e.target.value)}
                placeholder="6-digit pincode"
                className="w-36 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Classify as</label>
              <select
                value={newExceptionType}
                onChange={e => setNewExceptionType(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
              >
                <option value="instation">Instation</option>
                <option value="outstation">Outstation</option>
              </select>
            </div>
            <button onClick={addException} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800">
              Add Exception
            </button>
          </div>

          <button
            onClick={savePincodeConfig}
            disabled={pincodeSaving}
            className="px-6 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-500 disabled:opacity-50"
          >
            {pincodeSaving ? 'Saving...' : 'Save Pincode Rules'}
          </button>
        </div>
      )}

      {/* Company Name - Owner Only - shown on printed delivery labels */}
      {isOwner && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4 text-brand-700">🏢 Company Name</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            This name is printed on every delivery label and shown on invoice detail pages.
          </p>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="e.g. MANGAL DISTRIBUTORS, MYSURU"
            className="w-full p-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 mb-4"
          />
          <button
            onClick={handleUpdateCompanyName}
            disabled={savingCompanyName}
            className="px-6 py-3 bg-brand-700 text-white rounded-lg hover:bg-brand-500 disabled:bg-gray-400 font-semibold transition-colors"
          >
            {savingCompanyName ? 'Saving...' : 'Save Company Name'}
          </button>
        </div>
      )}

      {/* Non-Owner Message */}
      {!isOwner && (
        <div className="bg-yellow-50 rounded-lg shadow-lg p-6 border border-yellow-200">
          <h2 className="text-2xl font-bold mb-2 text-yellow-900">⚠️ Owner Access Required</h2>
          <p className="text-yellow-800">
            These settings are only available to owners.
            Your current role is: <strong>{user.role || 'unknown'}</strong>
          </p>
        </div>
      )}
    </div>
  );
}
